import { useEffect, useState } from "react";
import { Button, Container, Stack, Typography } from "@mui/material";
import { UUID } from "crypto";

import { useSelector } from "react-redux";
import { RootState } from "@store/store";

import { usePutApplicationsMutation } from "@services/api/applications";
import { OwnerAutocompleteInput } from "@components/OwnerAutocompleteInput";
import { AssignableOwner, useAssignableOwners } from "@hooks/useAssignableOwners";
import { useNotifyRequestAssigned } from "@hooks/useNotifyRequestAssigned";

export interface LoanAppOwnerAssignmentProps {
    applicationId: UUID;
    companyId: UUID;
    stageId: UUID;
    currentOwnerName?: string;
    onUpdateRow?: (applicationId: UUID, attr: {}) => void;
}

export const LoanAppOwnerAssignment = (props: LoanAppOwnerAssignmentProps) => {
    const userSession = useSelector((state: RootState) => state.session.user);

    const { loading, loadAssignableOwners } = useAssignableOwners();
    const [triggerPutApplication, { isLoading: isSaving }] = usePutApplicationsMutation();
    const { notifyRequestAssigned } = useNotifyRequestAssigned();

    const [options, setOptions] = useState<Array<AssignableOwner>>([]);
    const [userSelected, setUserSelected] = useState<AssignableOwner | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        loadAssignableOwners({
            companyId: props.companyId,
            stageId: props.stageId,
        })
            .then(setOptions)
            .catch(() => setOptions([]));
    }, [props.applicationId]);

    const handleOnConfirm = async () => {
        if (!userSelected) return;

        try {
            setErrorMessage(null);

            await triggerPutApplication({
                applicationId: props.applicationId,
                body: {
                    user_bpm_id: userSelected.id,
                    user_id_modified: userSession.id,
                    user_name:
                        userSession.name
                            ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                            .trim() ?? "",
                },
            }).unwrap();

            notifyRequestAssigned({
                loanApplicationId: props.applicationId,
                userAssigned: userSelected.id,
                userId: userSession.id,
            });

            props.onUpdateRow?.(props.applicationId, {
                user_bpm_id: userSelected.id,
                user_bpm: {
                    id: userSelected.id,
                    name: userSelected.name,
                    last_name: userSelected.last_name,
                    last_name_2: userSelected.last_name_2,
                },
            });
        } catch (error) {
            setErrorMessage("No se pudo asignar el usuario a la solicitud.");
        }
    };

    return (
        <Container maxWidth="xs" sx={{ mb: 3, minWidth: "280px" }}>
            <Stack spacing={2} sx={{ textAlign: "center", alignItems: "center" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#528CD6" }}>
                    Relación Owner
                </Typography>

                {props.currentOwnerName && (
                    <Typography variant="body2">
                        Actual: <b>{props.currentOwnerName}</b>
                    </Typography>
                )}

                <OwnerAutocompleteInput loading={loading} options={options} value={userSelected} onChange={setUserSelected} />

                {errorMessage && (
                    <Typography variant="body2" color="error">
                        {errorMessage}
                    </Typography>
                )}

                <Button variant="contained" disabled={!userSelected || isSaving} onClick={handleOnConfirm}>
                    Asignar
                </Button>
            </Stack>
        </Container>
    );
};
